<template>
  <div class="careers-portal">
    <header class="careers-header">
      <div class="header-content">
        <h1>Join Our Team</h1>
        <p>Explore open positions and start your career with us.</p>
        <div class="actions">
          <router-link to="/self-register" class="hr-btn hr-btn-secondary">Direct Self-Registration</router-link>
          <router-link to="/" class="hr-btn hr-btn-outline hr-ml-2">Back to Login</router-link>
        </div>
      </div>
    </header>

    <main class="careers-main">
      <div v-if="loading" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
      </div>
      <div v-else-if="error" class="error-msg">{{ error }}</div>
      <div v-else-if="!vacancies.length" class="empty-state">
        <i class="bx bx-briefcase hr-text-muted"></i>
        <h2>No open vacancies</h2>
        <p>There are currently no open positions. Please check back later.</p>
      </div>

      <div v-else class="vacancies-grid">
        <div v-for="v in vacancies" :key="v.id" class="vacancy-card">
          <div class="vacancy-header">
            <h3>{{ v.title }}</h3>
            <span class="hr-badge hr-badge-success">{{ v.employment_type }}</span>
          </div>
          <div class="vacancy-meta">
            <span><i class="bx bx-map"></i> {{ v.location || 'Remote/Office' }}</span>
            <span v-if="v.department_name"><i class="bx bx-building"></i> {{ v.department_name }}</span>
          </div>
          <p class="vacancy-desc">{{ v.description }}</p>
          <div class="vacancy-footer">
            <span class="closing-date">Closes: {{ formatDate(v.closing_date) }}</span>
            <button class="hr-btn hr-btn-primary" @click="openApplicationModal(v)">Apply Now</button>
          </div>
        </div>
      </div>
    </main>

    <!-- Application Modal -->
    <div v-if="showModal" class="modal-backdrop">
      <div class="modal-card">
        <div class="modal-header">
          <h2>Apply for {{ selectedVacancy?.title }}</h2>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitApplication" class="hr-form-grid">
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Full Name *</label>
              <input v-model="form.applicantName" type="text" class="hr-input" required />
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Email Address *</label>
              <input v-model="form.email" type="email" class="hr-input" required />
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Phone Number *</label>
              <input v-model="form.phone" type="tel" class="hr-input" required />
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">National ID</label>
              <input v-model="form.nationalId" type="text" class="hr-input" />
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Cover Letter</label>
              <textarea v-model="form.coverLetter" class="hr-textarea" rows="4"></textarea>
            </div>
            <div class="hr-filter-group span-full">
              <button type="submit" class="hr-btn hr-btn-primary hr-w-full" :disabled="submitting">
                {{ submitting ? 'Submitting...' : 'Submit Application' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute, execute2, toast } from "@/api";

export default {
  name: "CareersPortal",
  data() {
    return {
      loading: true,
      error: "",
      vacancies: [],
      showModal: false,
      selectedVacancy: null,
      submitting: false,
      form: {
        applicantName: "",
        email: "",
        phone: "",
        nationalId: "",
        coverLetter: ""
      }
    };
  },
  mounted() {
    this.fetchVacancies();
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      const d = new Date(dateStr);
      return isNaN(d) ? dateStr : d.toLocaleDateString();
    },
    fetchVacancies() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getPublicVacancies");
      // execute2 doesn't send token if public
      execute(data, authUrl).then(res => {
        this.loading = false;
        if (res.data.success) {
          this.vacancies = res.data.data || [];
        } else {
          this.error = "Failed to load vacancies.";
        }
      }).catch(err => {
        this.loading = false;
        // Fallback for public execution without token interceptors
        execute2(data, authUrl).then(res => {
            if (res.data.success) {
                this.vacancies = res.data.data || [];
                this.error = "";
            } else {
                this.error = "Failed to load vacancies.";
            }
        }).catch(() => {
            this.error = "Unable to reach server.";
        });
      });
    },
    openApplicationModal(vacancy) {
      this.selectedVacancy = vacancy;
      this.form = { applicantName: "", email: "", phone: "", nationalId: "", coverLetter: "" };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedVacancy = null;
    },
    submitApplication() {
      if (!this.form.applicantName || !this.form.email || !this.form.phone) {
        toast.warning("Please fill in all required fields.");
        return;
      }
      this.submitting = true;
      const data = new FormData();
      data.append("function", "submitPublicApplication");
      data.append("vacancyId", this.selectedVacancy.id);
      data.append("applicantName", this.form.applicantName);
      data.append("email", this.form.email);
      data.append("phone", this.form.phone);
      data.append("nationalId", this.form.nationalId);
      data.append("coverLetter", this.form.coverLetter);

      execute2(data, authUrl).then(res => {
        this.submitting = false;
        if (res.data.success) {
          toast.success("Application submitted successfully!");
          this.closeModal();
        } else {
          toast.error(res.data.message || "Submission failed.");
        }
      }).catch(() => {
        this.submitting = false;
        toast.error("An error occurred while submitting.");
      });
    }
  }
};
</script>

<style scoped>
.careers-portal {
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'Inter', 'DM Sans', sans-serif;
}

.careers-header {
  background: #152c56;
  color: white;
  padding: 60px 20px;
  text-align: center;
}

.header-content h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
}

.header-content p {
  color: #aab0bf;
  margin-bottom: 20px;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.careers-main {
  max-width: 1000px;
  margin: -30px auto 40px;
  padding: 0 20px;
}

.vacancies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.vacancy-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.vacancy-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.vacancy-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #1e293b;
}

.vacancy-meta {
  display: flex;
  gap: 15px;
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 15px;
}

.vacancy-desc {
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 20px;
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.vacancy-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  border-top: 1px solid #e2e8f0;
  padding-top: 15px;
}

.closing-date {
  font-size: 0.85rem;
  color: #94a3b8;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 20px;
}

/* Modal styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-card {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #1e293b;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
}

.modal-body {
  padding: 24px;
}

/* Base UI components from main css used here */
.hr-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  font-size: 0.9rem;
}

.hr-btn-primary { background: #4f46e5; color: white; }
.hr-btn-primary:hover { background: #4338ca; }
.hr-btn-secondary { background: #e0e7ff; color: #4338ca; }
.hr-btn-outline { background: transparent; border: 1px solid rgba(255,255,255,0.3); color: white; }
.hr-w-full { width: 100%; }
.hr-ml-2 { margin-left: 0.5rem; }

.hr-badge {
  padding: 4px 8px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.hr-badge-success { background: #dcfce7; color: #166534; }

.hr-form-grid { display: grid; gap: 15px; }
.hr-filter-group { display: flex; flex-direction: column; }
.span-full { grid-column: 1 / -1; }
.hr-form-label { font-size: 0.85rem; font-weight: 500; margin-bottom: 6px; color: #475569; }
.hr-input, .hr-textarea {
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-family: inherit;
}
.hr-input:focus, .hr-textarea:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
}
</style>
